class Event {
  name: string
  handlers: ((...args: any[]) => any)[]

  constructor(name: string) {
    this.name = name
    this.handlers = []
  }

  /**
   * Add handler to current Event
   */
  addHandler(handler: (...args: any[]) => any): void {
    this.handlers.push(handler.bind({
      unsubscribe: () => {
        this.removeHandler(handler)
      },
    }))
  }

  /**
   * Remove handler from current Event
   */
  removeHandler(handler: (...args: any[]) => any): void {
    const handlerIndex = this.handlers.indexOf(handler)

    this.handlers.splice(handlerIndex, 1)
  }

  /**
   * Call all handlers in all priorities of current Event
   */
  call(...args: any[]): void {
    this.handlers.forEach((handler) => {
      try {
        handler(...args)
      }
      catch (err) {
        console.error(err)
      }
    })
  }
}

class EventAggregator {
  events: { [name: string]: Event }

  constructor() {
    this.events = {}
  }

  /**
   * Get Event by name
   */
  getEvent(name: string): Event {
    let event = this.events[name]

    if (!event) {
      event = new Event(name)
      this.events[name] = event
    }

    return event
  }

  subscribe(name: string, handler: (...args: any[]) => any) {
    const event = this.getEvent(name)

    event.addHandler(handler)

    return { event, handler }
  }

  unsubscribe(name: string, handler: (...args: any[]) => any): void {
    const event = this.getEvent(name)

    event.removeHandler(handler)
  }

  dispatch(name: string, ...eventArgs: any[]): void {
    const event = this.getEvent(name)

    if (event) {
      event.call(...eventArgs)
    }
  }

  /**
   * Subscribe to Event and unsubscribe after call
   */
  once(name: string, handler: (...args: any[]) => any) {
    const event = this.getEvent(name)

    const handlerWrapper = (...args: any[]): void => {
      const result = handler(...args)
      if (result) {
        event.removeHandler(handlerWrapper)
      }
    }

    event.addHandler(handlerWrapper)

    return { event, handlerWrapper }
  }
}


export default new EventAggregator()
